import React from 'react';

import { splittedDataToSortedArray } from 'containers/pdfStudent/utils/splittedDataToSortedArray';
import { useAppSelector, useDataLoad } from 'hooks';
import { IconWidgetStudyGia } from 'icons/widgets';
import { chosenAcademicYearsListSelector } from 'portfolio3/features/pdf/model/selectors';
import { studyGiaListSelector } from 'selectors';
import { getAcademicYearByDate, getDataLoadRequests, isDateValid } from 'utils';

import { IGiaItem, ISectionsVisibility } from '../../../api/types';
import { GiaType } from '../../../const';
import PdfCommonDataEntityContainer from '../../../containers/pdfStudent/dataEntityContainers/common';
import AcademicYearTitle from '../AcademicYearTitle';
import ExamContainer from './ExamContainer';

import './index.scss';

interface IPdfGiaProps {
  displaySettings?: ISectionsVisibility;
}

type Exams = {
  ogeExams: IGiaItem[];
  approbationOgeExams: IGiaItem[];
  gve9Exams: IGiaItem[];
  egeExams: IGiaItem[];
  gve11Exams: IGiaItem[];
  otherExams: IGiaItem[];
};

const splitGiaByAcademicYear = (data: IGiaItem[], academicYears: string[]) => {
  const learningYearData: Record<string, Exams> = {};

  data.forEach((item) => {
    const date = new Date(item.date);

    if (!isDateValid(date)) {
      return;
    }

    const period = getAcademicYearByDate(date);
    if (academicYears.includes(period)) {
      if (!learningYearData[period]) {
        learningYearData[period] = {
          ogeExams: [],
          approbationOgeExams: [],
          gve9Exams: [],
          egeExams: [],
          gve11Exams: [],
          otherExams: [],
        };
      }
      switch (item.formaGia) {
        case GiaType.oge: {
          if (item.approbation) {
            learningYearData[period].approbationOgeExams.push(item);
          } else {
            learningYearData[period].ogeExams.push(item);
          }
          break;
        }
        case GiaType.gve9: {
          learningYearData[period].gve9Exams.push(item);
          break;
        }
        case GiaType.ege: {
          if (!item.approbation) {
            learningYearData[period].egeExams.push(item);
          }
          break;
        }
        case GiaType.gve11: {
          learningYearData[period].gve11Exams.push(item);
          break;
        }
        case GiaType.writing:
        case GiaType.narration:
        case GiaType.interviewRus: {
          learningYearData[period].otherExams.push(item);
          break;
        }
        default:
      }
    }
  });

  return splittedDataToSortedArray(learningYearData);
};

const PdfGia: React.FC<IPdfGiaProps> = ({ displaySettings }) => {
  const academicYears = useAppSelector(chosenAcademicYearsListSelector);
  useDataLoad({ shouldLoad: true, requests: getDataLoadRequests().studyGia });

  const studentGia = useAppSelector(studyGiaListSelector);

  const academicYearData = splitGiaByAcademicYear(studentGia, academicYears);

  return (
    <PdfCommonDataEntityContainer title="Государственная итоговая аттестация" Icon={IconWidgetStudyGia}>
      {academicYearData.map(([academicYear, data]) => (
        <div key={academicYear}>
          <AcademicYearTitle academicYear={academicYear} />
          {displaySettings?.ege && data.egeExams.length > 0 && (
            <ExamContainer title={GiaType.egeFullName} exams={data.egeExams} />
          )}
          {displaySettings?.oge && data.ogeExams.length > 0 && (
            <ExamContainer title={GiaType.ogeFullName} exams={data.ogeExams} />
          )}
          {displaySettings?.approbationOge && data.approbationOgeExams.length > 0 && (
            <ExamContainer title={GiaType.approbationOgeFullName} exams={data.approbationOgeExams} />
          )}
          {displaySettings?.gve9 && data.gve9Exams.length > 0 && (
            <ExamContainer title={GiaType.gve9FullName} exams={data.gve9Exams} />
          )}
          {displaySettings?.gve11 && data.gve11Exams.length > 0 && (
            <ExamContainer title={GiaType.gve11FullName} exams={data.gve11Exams} />
          )}
          {displaySettings?.other && data.otherExams.length > 0 && (
            <ExamContainer title={GiaType.other} exams={data.otherExams} />
          )}
        </div>
      ))}
    </PdfCommonDataEntityContainer>
  );
};

export default PdfGia;
